import React, { useEffect, useState } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { toast } from 'react-toastify';
import { store } from '../../redux/oldStore';

import { IFair, IFairPromotionInfo, IFairPromotionMappingInfo, IFeedRequest, IMappingKey, IPromotionRecord } from './PromotionTypes';
import Button from '../_Core/Button';
import Mapper from './Mapper';
import Icon, { IconType } from '../Icon/Icon';
import { IListItem } from '../_Core/CoreTypes';
import ParticipantList from './ParticipantList';
import { ServerResponse } from '../../utils/Server';

interface IProps {
  data: IFairPromotionInfo;
  onPromoted: () => void;
}

const PromoteWizard = (props: IProps) => {
  const [mappingKey, setMappingKey] = useState<IMappingKey>({});
  const [mappingData, setMappingData] = useState<IFairPromotionMappingInfo | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [mappingTabIndex, setMappingTabIndex] = useState(0);
  const [participantPool, setParticipantPool] = useState<IListItem[]>([]);

  useEffect(() => {
    // load mapping data based on DestinationId
    if (mappingKey.DestinationId > 0) {
      store.server
        .getApi2<IFairPromotionMappingInfo>(`/FairPromotion/MappingInfoForFair/${mappingKey.DestinationId}`)
        .then((x) => {
          // Handle the fetched data
          if (x.Success) {
            setMappingData(x.Value);
          } else {
            toast.error('Failed to fetch mapping data');
          }
        })
        .catch((error) => {
          // Handle errors
          console.error('Error fetching mapping data:', error);
        });
    } else {
      //do i clear stuff out...
      setMappingData(null);
    }
  }, [mappingKey.DestinationId]);

  useEffect(() => {
    if (mappingKey.PoolFilter) {
      store.server
        .getApi2<IListItem[]>(`/FairPromotion/ParticipantsToPromote?filter=${mappingKey.PoolFilter}`)
        .then((x) => {
          if (x.Success) {
            // Handle successful response
            setParticipantPool(x.Value);
            if(x.Value.length === 0) {
                toast.info('No participants found for the selected pool');
            } else {
                toast.success(`Fetched ${x.Value.length} participants to promote`);
            }
          } else {
            toast.error('Failed to fetch participants to promote');
            console.error('Failed to fetch participants to promote:', x);
          }
        })
        .catch((error) => {
          console.error('Error fetching participants to promote:', error);
          toast.error('Error fetching participants to promote');
        });
    } else {
      setParticipantPool([]);
    }
  }, [mappingKey.PoolFilter]);

  const handlePromote = () => {
    // Implement the promotion logic here
    const errors: string[] = [];
    if (!mappingKey.DestinationId) {
      errors.push('Destination event is required.');
    }
    if (!mappingKey.PoolFilter) {
      errors.push('Entry pool is required.');
    }

    if(!mappingKey.EntryIds || mappingKey.EntryIds.length === 0) {
      errors.push('At least one entry must be selected.');
    }

    let someMappingDone = false;
    //is at least some mapping done? 
    if(mappingKey.Categories && mappingKey.Categories.length > 0) {
      someMappingDone = true;
    }
    if(mappingKey.EntryTypes && mappingKey.EntryTypes.length > 0) {
      someMappingDone = true;
    }
    if(mappingKey.Paperwork && mappingKey.Paperwork.length > 0) {
      someMappingDone = true;
    }
    if(mappingKey.ParticipantQuestions && mappingKey.ParticipantQuestions.length > 0) {
        someMappingDone = true;
    }
    if(mappingKey.EntryQuestions && mappingKey.EntryQuestions.length > 0) {
        someMappingDone = true;
    }
    if(mappingKey.Packets && mappingKey.Packets.length > 0) {
      someMappingDone = true;
    }

    if(!someMappingDone) {
      if(!window.confirm('No field mappings have been done. This may result in incomplete or inaccurate data in the target event. Are you sure you want to proceed without mapping any fields?')) {
        errors.push('At least some mapping must be done.');
        return;
      }
    }

    if (errors.length > 0) {
      if(errors.length === 1) {
        toast.error(errors[0]);
        return;
      }
      toast.error(<div>
        <ul>
        {errors.map((error, index) => (
          <li key={index}>{error}</li>
        ))}
        </ul>
      </div>);
      return;
    }

    store.server.postApi<ServerResponse<IPromotionRecord>>(`/FairPromotion/PromoteTo`, mappingKey)
      .then((response) => {
        if (response.Success) {
          console.log('Promoting with mapping key:', mappingKey);
          toast.success('Advancements submitted successfully!');
          props.onPromoted();
        } else {
          toast.error('Failed to submit advancement');
          console.error('Failed to submit advancement:', response);
        }
      })
      .catch((error) => {
        console.error('Error submitting advancements:', error);
        toast.error('Error submitting advancements');
      });
  };

  return (
    <>
      <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
        <TabList>
          <Tab>Step 1: Select Entry Pool</Tab>
          <Tab>Step 2: Map Fields</Tab>
          <Tab>Step 3: Confirm Entries</Tab>
          <Tab>Step 4: Submit</Tab>
        </TabList>
        <TabPanel>
          <div className="form-horizontal">
            <h4>Step 1: Select Target & Entry Pool</h4>
            {/* Content for Step 1 */}
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="promote-target-event">
                Target Event
              </label>
              <div className="col-sm-8">
                <select className="form-control" id="promote-target-event" value={mappingKey.DestinationId} onChange={(e) => setMappingKey({ ...mappingKey, DestinationId: +e.target.value })}>
                  <option value="">Select a target event</option>
                  {/* Options will be populated dynamically */}
                  {props.data.promoteToEvents.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="control-label col-sm-4" htmlFor="promote-entry-pool">
                Minimum Award to Advance (Entry Pool)
              </label>
              <div className="col-sm-8">
                <select id="promote-entry-pool" className="form-control" value={mappingKey.PoolFilter} onChange={(e) => setMappingKey({ ...mappingKey, PoolFilter: e.target.value })}>
                  <option value="">Select an entry pool</option>
                  {/* Options will be populated dynamically */}
                  {props.data.promoteToFilterOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-offset-4 col-sm-8">
                <Button type="next" onClick={() => setTabIndex(tabIndex + 1)} text="Next" addClass='btn-primary'/>
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel>
          <div className="form-horizontal">
            <h4>Step 2: Map Fields</h4>
            {/* Content for Step 2 */}
            {mappingData && (
              <div>
                <Tabs selectedIndex={mappingTabIndex} onSelect={(index) => setMappingTabIndex(index)}>
                  <TabList>
                    {mappingData.mapCategories && <Tab>Categories</Tab>}
                    {mappingData.mapParticipantQuestions && <Tab>Participant Questions</Tab>}
                    {mappingData.mapEntryQuestions && <Tab>Entry Questions</Tab>}
                    {mappingData.mapEntryTypes && <Tab>Entry Types</Tab>}
                    {mappingData.mapPaperwork && <Tab>Paperwork</Tab>}
                    {mappingData.mapPackets && <Tab>Packets</Tab>}
                    <Tab>
                      <Icon type={IconType.checklist} /> Mapped Summary
                    </Tab>
                  </TabList>

                  {mappingData.mapCategories && (
                    <TabPanel>
                      <Mapper
                        sourceList={mappingData.categorySource}
                        destinationList={mappingData.categoryDestination}
                        mappingKey={mappingKey.Categories}
                        UpdatedMap={(newMap) => {
                          setMappingTabIndex(mappingTabIndex + 1);
                          setMappingKey({ ...mappingKey, Categories: newMap });
                        }}
                      />
                    </TabPanel>
                  )}
                  {mappingData.mapParticipantQuestions && (
                    <TabPanel>
                      <Mapper
                        sourceList={mappingData.participantQuestionsSource}
                        destinationList={mappingData.participantQuestionsDestination}
                        mappingKey={mappingKey.ParticipantQuestions}
                        UpdatedMap={(newMap) => {
                          setMappingTabIndex(mappingTabIndex + 1);
                          setMappingKey({ ...mappingKey, ParticipantQuestions: newMap });
                        }}
                      />
                    </TabPanel>
                  )}
                  {mappingData.mapEntryQuestions && (
                    <TabPanel>
                      <Mapper
                        sourceList={mappingData.entryQuestionsSource}
                        destinationList={mappingData.entryQuestionsDestination}
                        mappingKey={mappingKey.EntryQuestions}
                        UpdatedMap={(newMap) => {
                          setMappingTabIndex(mappingTabIndex + 1);
                          setMappingKey({ ...mappingKey, EntryQuestions: newMap });
                        }}
                      />
                    </TabPanel>
                  )}
                  {mappingData.mapEntryTypes && (
                    <TabPanel>
                      <Mapper
                        sourceList={mappingData.entryTypesSource}
                        destinationList={mappingData.entryTypesDestination}
                        mappingKey={mappingKey.EntryTypes}
                        UpdatedMap={(newMap) => {
                          setMappingTabIndex(mappingTabIndex + 1);
                          setMappingKey({ ...mappingKey, EntryTypes: newMap });
                        }}
                      />
                    </TabPanel>
                  )}
                  {mappingData.mapPaperwork && (
                    <TabPanel>
                      <Mapper
                        sourceList={mappingData.formsSource}
                        destinationList={mappingData.formsDestination}
                        mappingKey={mappingKey.Paperwork}
                        UpdatedMap={(newMap) => {
                          setMappingTabIndex(mappingTabIndex + 1);
                          setMappingKey({ ...mappingKey, Paperwork: newMap });
                        }}
                      />
                    </TabPanel>
                  )}
                  {mappingData.mapPackets && (
                    <TabPanel>
                      <Mapper
                        sourceList={mappingData.packetsSource}
                        destinationList={mappingData.packetsDestination}
                        mappingKey={mappingKey.Packets}
                        UpdatedMap={(newMap) => {
                          setMappingTabIndex(mappingTabIndex + 1);
                          setMappingKey({ ...mappingKey, Packets: newMap });
                        }}
                      />
                    </TabPanel>
                  )}
                  <TabPanel>
                    <h4>Mapping Summary</h4>
                    {mappingData.mapCategories && (
                      <div className="form-group">
                        <label className="control-label col-sm-4">Categories</label>
                        <div className="col-sm-8">
                          <div className="form-control">
                            {mappingKey.Categories?.length > 0 ? (
                              <>
                                {mappingKey.Categories.length} of {mappingData.categorySource?.length} Categories Mapped
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {mappingData.mapParticipantQuestions && (
                      <div className="form-group">
                        <label className="control-label col-sm-4">Participant Questions</label>
                        <div className="col-sm-8">
                          <div className="form-control">
                            {mappingKey.ParticipantQuestions?.length > 0 ? (
                              <>
                                {mappingKey.ParticipantQuestions.length} of {mappingData.participantQuestionsSource?.length} Participant Questions Mapped
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {mappingData.mapEntryQuestions && (
                      <div className="form-group">
                        <label className="control-label col-sm-4">Entry Questions</label>
                        <div className="col-sm-8">
                          <div className="form-control">
                            {mappingKey.EntryQuestions?.length > 0 ? (
                              <>
                                {mappingKey.EntryQuestions.length} of {mappingData.entryQuestionsSource.length} Entry Questions Mapped
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {mappingData.mapEntryTypes && (
                      <div className="form-group">
                        <label className="control-label col-sm-4">Entry Types</label>
                        <div className="col-sm-8">
                          <div className="form-control">
                            {mappingKey.EntryTypes?.length > 0 ? (
                              <>
                                {mappingKey.EntryTypes.length} of {mappingData.entryTypesSource.length} Entry Types Mapped
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {mappingData.mapPaperwork && (
                      <div className="form-group">
                        <label className="control-label col-sm-4">Paperwork</label>
                        <div className="col-sm-8">
                          <div className="form-control">
                            {mappingKey.Paperwork?.length > 0 ? (
                              <>
                                {mappingKey.Paperwork.length} of {mappingData.formsSource.length} Paperwork Forms Mapped
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {mappingData.mapPackets && (
                      <div className="form-group">
                        <label className="control-label col-sm-4">Packets</label>
                        <div className="col-sm-8">
                          <div className="form-control">
                            {mappingKey.Packets?.length > 0 ? (
                              <>
                                {mappingKey.Packets.length} of {mappingData.packetsSource.length} Packets Mapped
                              </>
                            ) : (
                              <></>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <div className="col-sm-offset-4 col-sm-8">
                        <div className="btn-group">
                          <Button type="previous" onClick={() => setTabIndex(tabIndex - 1)} text="Previous" />
                          <Button type="next" onClick={() => setTabIndex(tabIndex + 1)} addClass='btn-primary' text="Next" />
                        </div>
                      </div>
                    </div>
                  </TabPanel>
                </Tabs>
              </div>
            )}
          </div>
        </TabPanel>
        <TabPanel>
          <div className="form-horizontal">
            <h4>Step 3: Confirm Entries</h4>
            {/* Content for Step 3 */}
            <ParticipantList
                participants={participantPool}
                selectedParticipant={mappingKey.EntryIds || []}
                Back={()=>{ setTabIndex(tabIndex - 1)}}
                updateSelected={(selected) => {
                    // Handle the selected participants                    
                    setMappingKey({...mappingKey, EntryIds: selected});
                    console.log('Selected participants:', selected);
                    setTabIndex(tabIndex + 1);
                }}
            />
          </div>
        </TabPanel>
        <TabPanel>
          <div className="form-horizontal">
            <h4>Step 4: Submit</h4>
            {/* Content for Step 4 */}
            <h5><Icon type={IconType.checklist} /> Promote Checklist</h5>
            <ol>
                <li><strong>Select Entry Pool</strong>
                    <ul>
                        <li>Target Event: {props.data.promoteToEvents.find(e => e.id === mappingKey.DestinationId+'')?.name || 'Not Selected'}</li>
                        <li>Minimum Award to Advance (Entry Pool): {props.data.promoteToFilterOptions.find(p => p.id === mappingKey.PoolFilter)?.name || 'Not Selected'}</li>
                    </ul>
                </li>
                <li><strong>Map Fields</strong>
                    <ul>
                        <li>Mapped Categories: {mappingKey.Categories?.length || 0}</li>
                        <li>Mapped Participant Custom Questions: {mappingKey.ParticipantQuestions?.length || 0}</li>
                        <li>Mapped Entry Custom Questions: {mappingKey.EntryQuestions?.length || 0}</li>
                        <li>Mapped Entry Types: {mappingKey.EntryTypes?.length || 0}</li>
                        <li>Mapped Forms: {mappingKey.Paperwork?.length || 0}</li>
                        <li>Mapped Packets: {mappingKey.Packets?.length || 0}</li>
                    </ul>
                </li>
                <li><strong>Confirm Entries</strong>
                    <ul>
                        <li>Selected Participants: {mappingKey.EntryIds?.length || 0}</li>
                    </ul>

                </li>
            </ol>

            <Button type="approve" onClick={handlePromote} addClass="btn-primary" text="Submit Advancements" />
          </div>
        </TabPanel>
      </Tabs>
    </>
  );
};

export default PromoteWizard;
